import {
  api,
  type EllenorzesNap,
  type PenzTetel,
  type SoforAllapot,
  type SoforOsszesites,
} from '../api.ts';
import { ft, szam } from '../formazas.ts';
import { ablakotBezar, ablakotNyit, dokumentumMegjelenites, esc, huDatum } from './dokumentum-ablak.ts';

/**
 * A sofőri elszámolás nyomtatható lapja. (javitasok-2.0-teljes.md 6.)
 *
 * Külön ablakban nyílik, saját tiszta stíluslappal — nem a fő felület
 * nyomtatási CSS-ére épül. Egy sofőr nézetében az ő lapja készül, az
 * Összesítő ellenőrzésen minden kitöltött sofőré, egymás után, külön lapokon.
 *
 * Minden szám a szerver összesítéséből jön; itt csak elrendezés van.
 */

/** Az a része az alapadatoknak, ami a laphoz kell (kategória- és fizetésimód-nevek). */
export interface DokumentumAlapadatok {
  kategoriak: Array<{ id: number; nev: string }>;
  fizetesi_modok: Array<{ kod: string; nev: string }>;
  afa_kulcs: number | string;
}

/** Előjeles forint: a negatív összeg elé valódi mínuszjel kerül. */
export function elojeles(n: number): string {
  return n < 0 ? `−${ft(-n)}` : ft(n);
}

/** Az egyéb (nem HSO) kiadás kategóriánként, egy sorban — súgónak. */
export function kiadasBontasSzoveg(o: SoforOsszesites): string {
  const tetelek = egyebKiadasok(o);
  return tetelek.length === 0
    ? 'Nincs egyéb kiadás.'
    : tetelek.map((k) => `${k.kategoria_nev}: ${ft(k.osszeg)} Ft`).join('\n');
}

function egyebKiadasok(o: SoforOsszesites) {
  return o.kiadas_bontas.filter(
    (k) => k.kategoria_nev.trim().toUpperCase() !== 'HSO' && k.osszeg,
  );
}

const CSS = `
  .lap.elsz { font: 11.5px/1.4 "Segoe UI", system-ui, -apple-system, sans-serif; padding: 12mm 13mm; }
  .efej { display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
  .efej b { font-size: 16px; }
  .efej .jobb { text-align: right; }
  .efej .all { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #555; }
  .efej .all.nyitott { color: #8A5A00; }
  .eketto { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .elsz h3 { font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
             margin: 12px 0 4px; padding-bottom: 2px; border-bottom: 1px solid #000; }
  .eketto > div > h3:first-child { margin-top: 0; }
  table.et2 { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  table.et2 td { padding: 2.5px 0; border-bottom: 1px solid #eee; }
  table.et2 td.r { text-align: right; font-family: ui-monospace, Menlo, Consolas, monospace;
                   white-space: nowrap; font-variant-numeric: tabular-nums; }
  table.et2 tr.sub td { font-size: 10.5px; color: #666; padding-left: 10px; }
  table.et2 tr.tot td { font-weight: 700; border-top: 1.5px solid #000; border-bottom: 0; padding-top: 4px; }
  table.et2 tr.elt td { color: #a8281f; font-weight: 700; }
  table.et2 td.ures { color: #999; font-style: italic; font-size: 10.5px; }
  table.et2 i { font-style: normal; color: #777; font-size: 10px; }
  table.etet { width: 100%; border-collapse: collapse; font-size: 10.5px; margin-top: 2px; }
  table.etet th { background: #f1f2ef; border: 1px solid #bbb; padding: 3px 5px; text-align: left;
                  font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; }
  table.etet td { border: 1px solid #ccc; padding: 2.5px 5px; }
  table.etet td.r, table.etet th.r { text-align: right; font-family: ui-monospace, Menlo, Consolas, monospace;
                                     font-variant-numeric: tabular-nums; }
  table.etet b.fp { font-size: 9px; border: 1px solid #1C4C63; color: #1C4C63; padding: 0 3px; margin-left: 4px; }
  .elab { margin-top: 14px; font-size: 9.5px; color: #777; border-top: 1px solid #ddd; padding-top: 6px; }
  @media print { .lap.elsz { padding: 0; } }

  /* --- rövid napi kimutatás: szellős, nagy számok, egy oldal (2026-09-22) --- */
  .lap.rovid { font: 13px/1.4 "Segoe UI", system-ui, -apple-system, sans-serif; padding: 16mm 16mm; }
  .rovid .rfej { display: flex; justify-content: space-between; align-items: baseline;
                 border-bottom: 2.5px solid #000; padding-bottom: 10px; margin-bottom: 22px; }
  .rovid .rfej b { font-size: 22px; }
  .rovid .rfej .datum { font-size: 20px; font-weight: 700; }
  .rovid .rfej .all { display: block; text-align: right; font-size: 11px; text-transform: uppercase;
                      letter-spacing: .08em; color: #555; margin-top: 2px; }
  .rovid .rfej .all.nyitott { color: #8A5A00; }
  .rovid .racs { display: grid; grid-template-columns: 1fr 1fr; gap: 0 28px; }
  .rovid .tetel { display: flex; justify-content: space-between; align-items: baseline;
                  padding: 9px 0 7px; border-bottom: 1px solid #ccc; }
  .rovid .tetel em { font-style: normal; font-size: 12px; font-weight: 700; letter-spacing: .08em;
                     text-transform: uppercase; }
  .rovid .tetel em small { display: block; font-size: 11px; font-weight: 400; letter-spacing: 0;
                           text-transform: none; color: #666; margin-top: 1px; }
  .rovid .tetel span { font-family: ui-monospace, Menlo, Consolas, monospace; font-variant-numeric: tabular-nums;
                       font-size: 24px; font-weight: 700; white-space: nowrap; }
  .rovid .tetel span i { font-style: normal; font-size: 14px; font-weight: 400; margin-left: 4px; }
  .rovid .leado { margin: 20px 0 8px; border: 2.5px solid #000; padding: 14px 18px;
                  display: flex; justify-content: space-between; align-items: center; }
  .rovid .leado em { font-style: normal; font-size: 18px; font-weight: 800; letter-spacing: .1em; }
  .rovid .leado span { font-family: ui-monospace, Menlo, Consolas, monospace; font-variant-numeric: tabular-nums;
                       font-size: 36px; font-weight: 800; white-space: nowrap; }
  .rovid .leado span i { font-style: normal; font-size: 18px; font-weight: 400; margin-left: 6px; }
  .rovid .leadott { display: flex; justify-content: flex-end; gap: 28px; font-size: 13px; color: #333; }
  .rovid .leadott b { font-family: ui-monospace, Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
  .rovid .leadott .elt { color: #a8281f; }
  .rovid h3 { font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
              margin: 20px 0 5px; }
  .rovid table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .rovid th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em;
              border-bottom: 1.5px solid #000; padding: 5px 6px; }
  .rovid td { padding: 7px 6px; border-bottom: 1px solid #ddd; }
  .rovid td.r, .rovid th.r { text-align: right; font-family: ui-monospace, Menlo, Consolas, monospace;
                             font-variant-numeric: tabular-nums; white-space: nowrap; }
  .rovid tr.ossz td { font-weight: 700; border-top: 1.5px solid #000; border-bottom: 0; }
  .rovid td .elt { color: #a8281f; font-weight: 700; }
  .rovid .elab { margin-top: 22px; font-size: 10px; color: #777; border-top: 1px solid #ddd; padding-top: 6px; }
  @media print { .lap.rovid { padding: 0; } }
`;

function sor(cimke: string, ertek: string, osztaly = ''): string {
  return `<tr class="${osztaly}"><td>${cimke}</td><td class="r">${ertek}</td></tr>`;
}

function elteresSzoveg(n: number, egyseg: string): string {
  return `${n > 0 ? '+' : ''}${egyseg === 'l' ? szam(n, 1) : elojeles(n)} ${egyseg === 'l' ? 'l' : 'Ft'}`;
}

/** Egy sofőr lapja. */
function lap(
  datum: string,
  ellenorzes: EllenorzesNap,
  s: SoforAllapot,
  tetelek: PenzTetel[],
  alap: DokumentumAlapadatok,
  keszult: string,
): string {
  const o = s.osszesites;
  const k = s.keszlet;
  const p = s.penz;
  const kategoriaNev = (id: number | null) =>
    alap.kategoriak.find((x) => x.id === id)?.nev ?? 'Besorolás nélkül';
  const modNev = (kod: string | null) =>
    alap.fizetesi_modok.find((m) => m.kod === kod)?.nev ?? kod ?? '';

  const tetelSorok = (tipus: 'beszedes' | 'kiadas') => {
    const lista = tetelek.filter((t) => t.tipus === tipus && t.osszeg);
    if (lista.length === 0) return '<tr><td colspan="2" class="ures">nincs tétel</td></tr>';
    return lista
      .map(
        (t) =>
          `<tr><td>${esc(kategoriaNev(t.kategoria_id))}` +
          (t.fizetesi_mod && t.fizetesi_mod !== 'KP' ? ` <i>(${esc(modNev(t.fizetesi_mod))})</i>` : '') +
          (t.mennyiseg_kg ? ` <i>${szam(t.mennyiseg_kg, 0)} kg</i>` : '') +
          (t.mennyiseg_db ? ` <i>${szam(t.mennyiseg_db, 0)} hordó</i>` : '') +
          (t.rendszam ? ` <i>${esc(t.rendszam)}</i>` : '') +
          `</td><td class="r">${ft(t.osszeg)}</td></tr>`,
      )
      .join('');
  };

  const cimek = ellenorzes.sorok.filter((x) => x.sofor_id === s.sofor_id);
  const nap = huDatum(datum);

  return (
    `<div class="lap elsz">` +
    `<div class="efej"><div><b>Sofőri elszámolás</b><br>${esc(s.nev)}` +
    (s.rendszam ? ` · ${esc(s.rendszam)}` : '') +
    `</div><div class="jobb">${esc(nap)}<br><span class="all${s.lezarva ? '' : ' nyitott'}">` +
    `${s.lezarva ? 'lezárva' : 'ellenőrzés alatt'}</span></div></div>` +
    `<div class="eketto"><div>` +
    `<h3>Olajeladás</h3><table class="et2">` +
    sor('Liter', `${szam(o.liter, 1)} l`) +
    sor('ebből F+', `${szam(o.fplusz_liter, 1)} l`, 'sub') +
    sor('Bruttó eladás', `${ft(o.eladas)} Ft`) +
    sor(`ebből áfa ${esc(alap.afa_kulcs)}%`, `${ft(o.afa)} Ft`, 'sub') +
    sor('Átlagár', o.atlagar === null ? '—' : `${szam(o.atlagar, 2)} Ft/l`, 'sub') +
    sor('Nem készpénzes', `${ft(o.nem_keszpenzes)} Ft`) +
    sor('Raktári (R)', `${szam(o.raktari_liter, 1)} l`, 'sub') +
    `</table>` +
    `<h3>Moslék és göngyöleg</h3><table class="et2">` +
    sor('Moslék összeg', `${ft(o.mo_osszeg)} Ft`) +
    sor(`${szam(o.mo_db, 0)} db · ebből készpénzes`, `${ft(o.mo_keszpenzes)} Ft`, 'sub') +
    sor('Kanna betétdíj (kp)', `${elojeles(o.kanna_keszpenzes)} Ft`) +
    sor('kiadott / visszavett', `${szam(o.kanna_ki, 0)} / ${szam(o.kanna_vissza, 0)} db`, 'sub') +
    sor('Hordó betétdíj (kp)', `${ft(o.hordo_keszpenzes)} Ft`) +
    sor('kihelyezett hordó', `${szam(o.hordo_db, 0)} db`, 'sub') +
    sor('HSO kifizetés', `${ft(o.hso_osszeg)} Ft`) +
    sor('ebből súly', `${szam(o.hso_kg, 0)} kg`, 'sub') +
    `</table></div><div>` +
    `<h3>Beszedés</h3><table class="et2">${tetelSorok('beszedes')}</table>` +
    `<h3>Kiadás</h3><table class="et2">${tetelSorok('kiadas')}</table>` +
    `<h3>Autókészlet</h3><table class="et2">` +
    sor('Nyitó', `${szam(k.nyito, 1)} l`) +
    sor('Felrakodás', `${szam(k.felrakodas ?? 0, 1)} l`) +
    sor('Átadás másik autónak', `${szam(k.atadas ?? 0, 1)} l`) +
    sor('Kiszállítva', `${szam(k.kiszallitva, 1)} l`) +
    sor('Számított záró', `${szam(k.szamitott_zaro, 1)} l`, 'tot') +
    sor('Megszámolt záró', k.megszamolt_zaro === null ? '—' : `${szam(k.megszamolt_zaro, 1)} l`) +
    sor(
      'Eltérés',
      k.elteres === null ? '—' : elteresSzoveg(k.elteres, 'l'),
      k.elteres !== null && !k.rendben ? 'elt' : '',
    ) +
    `</table></div></div>` +
    `<h3>Leadó levezetése</h3><table class="et2">` +
    sor('Készpénzes olajeladás', `${ft(o.keszpenzes)} Ft`) +
    sor('+ moslék készpénzben', `${ft(o.mo_keszpenzes)} Ft`) +
    sor('+ kanna készpénzben', `${elojeles(o.kanna_keszpenzes)} Ft`) +
    sor('+ hordó készpénzben', `${ft(o.hordo_keszpenzes)} Ft`) +
    sor('+ egyéb beszedés készpénzben', `${ft(o.beszedes_keszpenzes)} Ft`) +
    sor(
      '− kiadás készpénzben' +
        (o.kiadas !== o.kiadas_keszpenzes ? ` <i>(összes kiadás ${ft(o.kiadas)} Ft)</i>` : ''),
      `${ft(o.kiadas_keszpenzes)} Ft`,
    ) +
    sor('LEADÓ', `${ft(o.leado)} Ft`, 'tot') +
    sor('Ténylegesen leadott', p.leadott_keszpenz === null ? '—' : `${ft(p.leadott_keszpenz)} Ft`) +
    sor(
      'Eltérés',
      p.elteres === null ? '—' : elteresSzoveg(p.elteres, 'Ft'),
      p.elteres !== null && !p.rendben ? 'elt' : '',
    ) +
    `</table>` +
    `<h3>Címek (${cimek.length})</h3>` +
    `<table class="etet"><tr><th>#</th><th>Étterem</th><th class="r">MO</th><th class="r">FO</th>` +
    `<th class="r">HO</th><th class="r">Liter</th><th class="r">Ár</th><th class="r">Összeg</th>` +
    `<th>Fiz.</th></tr>` +
    cimek
      .map(
        (c, i) =>
          `<tr><td>${i + 1}</td><td>${esc(c.etterem_nev)}${c.fplusz ? '<b class="fp">F+</b>' : ''}</td>` +
          `<td class="r">${c.teny_mo ? szam(c.teny_mo, 0) : ''}</td>` +
          `<td class="r">${c.teny_fo ? szam(c.teny_fo, 0) : ''}</td>` +
          `<td class="r">${c.teny_hordo ? szam(c.teny_hordo, 0) : ''}</td>` +
          `<td class="r">${c.teny_liter ? szam(c.teny_liter, 1) : ''}</td>` +
          `<td class="r">${c.egysegar ? ft(c.egysegar) : ''}</td>` +
          `<td class="r">${c.osszeg ? ft(c.osszeg) : ''}</td>` +
          `<td>${c.fizetesi_mod && c.fizetesi_mod !== 'KP' ? esc(c.fizetesi_mod) : ''}</td></tr>`,
      )
      .join('') +
    `</table>` +
    `<div class="elab">Készült: ${esc(keszult)} · ${esc(s.nev)} elszámolása · ${esc(nap)}</div>` +
    `</div>`
  );
}

/**
 * A nap összesítő lapja. (A megrendelő kérése, 2026-09-15)
 *
 * Ugyanaz a forma, mint a sofőri lapé, csak minden kitöltött sofőr együtt:
 * a számok a szerver `osszesito_mind` összesítéséből jönnek. A tételes
 * beszedés és kiadás helyett kategóriánkénti összeg áll, a címlista helyett
 * sofőrönkénti áttekintés és autókészlet.
 */
function osszesitoLap(
  datum: string,
  ellenorzes: EllenorzesNap,
  soforok: SoforAllapot[],
  alap: DokumentumAlapadatok,
  keszult: string,
): string {
  const o = ellenorzes.osszesito_mind!;
  const nap = huDatum(datum);
  const lezartDb = soforok.filter((s) => s.lezarva).length;
  const napLezarva = ellenorzes.nap.statusz === 'lezarva';
  const osszeg = (f: (s: SoforAllapot) => number | null) =>
    soforok.reduce((a, s) => a + (f(s) ?? 0), 0);

  const kategoriaSorok = (lista: SoforOsszesites['beszedes_bontas']) => {
    const vannak = lista.filter((k) => k.osszeg);
    if (vannak.length === 0) return '<tr><td colspan="2" class="ures">nincs tétel</td></tr>';
    return vannak
      .map(
        (k) =>
          `<tr><td>${esc(k.kategoria_nev)}` +
          (k.keszpenzes !== k.osszeg ? ` <i>(készpénzben ${ft(k.keszpenzes)} Ft)</i>` : '') +
          `</td><td class="r">${ft(k.osszeg)} Ft</td></tr>`,
      )
      .join('');
  };

  const leadottHianyzik = soforok.filter((s) => s.penz.leadott_keszpenz === null).length;
  const leadott = osszeg((s) => s.penz.leadott_keszpenz);
  const penzElteres = leadottHianyzik === 0 ? leadott - o.leado : null;

  const keszletSor = (
    cimke: string,
    k: { nyito: number; fel: number; at: number; ki: number; szamitott: number; megszamolt: number | null },
    osztaly = '',
  ) => {
    const elteres = k.megszamolt === null ? null : k.megszamolt - k.szamitott;
    return (
      `<tr class="${osztaly}"><td>${cimke}</td><td class="r">${szam(k.nyito, 1)}</td>` +
      `<td class="r">${szam(k.fel, 1)}</td><td class="r">${szam(k.at, 1)}</td>` +
      `<td class="r">${szam(k.ki, 1)}</td><td class="r">${szam(k.szamitott, 1)}</td>` +
      `<td class="r">${k.megszamolt === null ? '—' : szam(k.megszamolt, 1)}</td>` +
      `<td class="r">${elteres === null ? '—' : (elteres > 0 ? '+' : '') + szam(elteres, 1)}</td></tr>`
    );
  };
  const mindMegszamolva = soforok.every((s) => s.keszlet.megszamolt_zaro !== null);

  return (
    `<div class="lap elsz">` +
    `<div class="efej"><div><b>Napi elszámolás — összesítő</b><br>${soforok.length} sofőr</div>` +
    `<div class="jobb">${esc(nap)}<br><span class="all${napLezarva ? '' : ' nyitott'}">` +
    `${napLezarva ? 'lezárva' : `ellenőrzés alatt · ${lezartDb}/${soforok.length} sofőr lezárva`}` +
    `</span></div></div>` +
    `<div class="eketto"><div>` +
    `<h3>Olajeladás</h3><table class="et2">` +
    sor('Liter', `${szam(o.liter, 1)} l`) +
    sor('ebből F+', `${szam(o.fplusz_liter, 1)} l`, 'sub') +
    sor('Bruttó eladás', `${ft(o.eladas)} Ft`) +
    sor(`ebből áfa ${esc(alap.afa_kulcs)}%`, `${ft(o.afa)} Ft`, 'sub') +
    sor('Átlagár', o.atlagar === null ? '—' : `${szam(o.atlagar, 2)} Ft/l`, 'sub') +
    sor('Nem készpénzes', `${ft(o.nem_keszpenzes)} Ft`) +
    sor('Raktári (R)', `${szam(o.raktari_liter, 1)} l`, 'sub') +
    `</table>` +
    `<h3>Moslék és göngyöleg</h3><table class="et2">` +
    sor('Moslék összeg', `${ft(o.mo_osszeg)} Ft`) +
    sor(`${szam(o.mo_db, 0)} db · ebből készpénzes`, `${ft(o.mo_keszpenzes)} Ft`, 'sub') +
    sor('Kanna betétdíj (kp)', `${elojeles(o.kanna_keszpenzes)} Ft`) +
    sor('kiadott / visszavett', `${szam(o.kanna_ki, 0)} / ${szam(o.kanna_vissza, 0)} db`, 'sub') +
    sor('Hordó betétdíj (kp)', `${ft(o.hordo_keszpenzes)} Ft`) +
    sor('kihelyezett hordó', `${szam(o.hordo_db, 0)} db`, 'sub') +
    sor('HSO kifizetés', `${ft(o.hso_osszeg)} Ft`) +
    sor('ebből súly', `${szam(o.hso_kg, 0)} kg`, 'sub') +
    `</table></div><div>` +
    `<h3>Beszedés</h3><table class="et2">${kategoriaSorok(o.beszedes_bontas)}` +
    sor('Összesen', `${ft(o.beszedes)} Ft`, 'tot') +
    `</table>` +
    `<h3>Kiadás</h3><table class="et2">${kategoriaSorok(o.kiadas_bontas)}` +
    sor('Összesen', `${ft(o.kiadas)} Ft`, 'tot') +
    sor('ebből egyéb kiadás (HSO nélkül)', `${ft(o.egyeb_kiadas)} Ft`, 'sub') +
    `</table>` +
    (o.tankolasok.length > 0
      ? `<h3>Tankolások</h3><table class="et2">` +
        o.tankolasok
          .map((t) =>
            sor(
              `${esc(t.sofor_nev)} · ${t.rendszam ? esc(t.rendszam) : '<i>nincs rendszám</i>'}` +
                (t.fizetesi_mod && t.fizetesi_mod !== 'KP'
                  ? ` <i>(${esc(alap.fizetesi_modok.find((m) => m.kod === t.fizetesi_mod)?.nev ?? t.fizetesi_mod)})</i>`
                  : ''),
              `${ft(t.osszeg)} Ft`,
            ),
          )
          .join('') +
        sor('Összesen', `${ft(o.tankolas_osszeg)} Ft`, 'tot') +
        `</table>`
      : '') +
    `<h3>Leadó levezetése</h3><table class="et2">` +
    sor('Készpénzes olajeladás', `${ft(o.keszpenzes)} Ft`) +
    sor('+ moslék készpénzben', `${ft(o.mo_keszpenzes)} Ft`) +
    sor('+ kanna készpénzben', `${elojeles(o.kanna_keszpenzes)} Ft`) +
    sor('+ hordó készpénzben', `${ft(o.hordo_keszpenzes)} Ft`) +
    sor('+ egyéb beszedés készpénzben', `${ft(o.beszedes_keszpenzes)} Ft`) +
    sor('− kiadás készpénzben', `${ft(o.kiadas_keszpenzes)} Ft`) +
    sor('LEADÓ', `${ft(o.leado)} Ft`, 'tot') +
    sor(
      'Ténylegesen leadott' +
        (leadottHianyzik ? ` <i>(${leadottHianyzik} sofőrnél még nincs beírva)</i>` : ''),
      `${ft(leadott)} Ft`,
    ) +
    sor(
      'Eltérés',
      penzElteres === null ? '—' : elteresSzoveg(penzElteres, 'Ft'),
      penzElteres !== null && Math.abs(penzElteres) >= 1 ? 'elt' : '',
    ) +
    `</table></div></div>` +
    `<h3>Sofőrönként</h3>` +
    `<table class="etet"><tr><th>Sofőr</th><th class="r">Cím</th><th class="r">Liter</th>` +
    `<th class="r">F+ liter</th><th class="r">Eladás</th><th class="r">Egyéb kiadás</th>` +
    `<th class="r">Leadó</th><th class="r">Leadott</th><th>Állapot</th></tr>` +
    soforok
      .map(
        (s) =>
          `<tr><td>${esc(s.nev)}${s.rendszam ? ` <i>${esc(s.rendszam)}</i>` : ''}</td>` +
          `<td class="r">${s.sorok}</td><td class="r">${szam(s.osszesites.liter, 1)}</td>` +
          `<td class="r">${szam(s.osszesites.fplusz_liter, 1)}</td>` +
          `<td class="r">${ft(s.osszesites.eladas)}</td>` +
          `<td class="r">${ft(s.osszesites.egyeb_kiadas)}</td>` +
          `<td class="r">${ft(s.osszesites.leado)}</td>` +
          `<td class="r">${s.penz.leadott_keszpenz === null ? '—' : ft(s.penz.leadott_keszpenz)}</td>` +
          `<td>${s.lezarva ? 'lezárva' : 'ellenőrzés alatt'}</td></tr>`,
      )
      .join('') +
    `<tr><td><b>Összesen</b></td><td class="r"><b>${osszeg((s) => s.sorok)}</b></td>` +
    `<td class="r"><b>${szam(o.liter, 1)}</b></td><td class="r"><b>${szam(o.fplusz_liter, 1)}</b></td>` +
    `<td class="r"><b>${ft(o.eladas)}</b></td><td class="r"><b>${ft(o.egyeb_kiadas)}</b></td>` +
    `<td class="r"><b>${ft(o.leado)}</b></td><td class="r"><b>${ft(leadott)}</b></td><td></td></tr>` +
    `</table>` +
    `<h3>Autókészlet</h3>` +
    `<table class="etet"><tr><th>Sofőr</th><th class="r">Nyitó</th><th class="r">Felrakodás</th>` +
    `<th class="r">Átadás</th><th class="r">Kiszállítva</th><th class="r">Számított záró</th>` +
    `<th class="r">Megszámolt záró</th><th class="r">Eltérés</th></tr>` +
    soforok
      .map((s) =>
        keszletSor(esc(s.nev), {
          nyito: s.keszlet.nyito,
          fel: s.keszlet.felrakodas ?? 0,
          at: s.keszlet.atadas ?? 0,
          ki: s.keszlet.kiszallitva,
          szamitott: s.keszlet.szamitott_zaro,
          megszamolt: s.keszlet.megszamolt_zaro,
        }),
      )
      .join('') +
    keszletSor('<b>Összesen</b>', {
      nyito: osszeg((s) => s.keszlet.nyito),
      fel: osszeg((s) => s.keszlet.felrakodas),
      at: osszeg((s) => s.keszlet.atadas),
      ki: osszeg((s) => s.keszlet.kiszallitva),
      szamitott: osszeg((s) => s.keszlet.szamitott_zaro),
      megszamolt: mindMegszamolva ? osszeg((s) => s.keszlet.megszamolt_zaro) : null,
    }) +
    `</table>` +
    `<div class="elab">Készült: ${esc(keszult)} · napi összesítő elszámolás · ${esc(nap)}</div>` +
    `</div>`
  );
}

/**
 * Rövid napi kimutatás. (A megrendelő kérése, 2026-09-22)
 *
 * Az összesítő lap mellé, külön oldalon: csak a lényeg, szellősen, nagy
 * számokkal — liter, összeg, áfa, utalás, moslék, HSO, kiadás, a leadó
 * kiemelve, alatta sofőrönként egy sor. A részletes lap változatlan marad.
 */
function rovidLap(
  datum: string,
  ellenorzes: EllenorzesNap,
  soforok: SoforAllapot[],
  alap: DokumentumAlapadatok,
  keszult: string,
): string {
  const o = ellenorzes.osszesito_mind!;
  const nap = huDatum(datum);
  const napLezarva = ellenorzes.nap.statusz === 'lezarva';
  const lezartDb = soforok.filter((s) => s.lezarva).length;

  const tetel = (cimke: string, alcim: string, ertek: string, egyseg: string) =>
    `<div class="tetel"><em>${cimke}${alcim ? `<small>${alcim}</small>` : ''}</em>` +
    `<span>${ertek}<i>${egyseg}</i></span></div>`;

  const leadottHianyzik = soforok.filter((s) => s.penz.leadott_keszpenz === null).length;
  const leadott = soforok.reduce((a, s) => a + (s.penz.leadott_keszpenz ?? 0), 0);
  const elteres = leadottHianyzik === 0 ? leadott - o.leado : null;
  /** A kiadás tételesen, a HSO nélkül — az „Egyéb" a beírt szövegével áll. */
  const kiadasTetelek = egyebKiadasok(o);
  const elterEPenz = (s: SoforAllapot) => s.penz.leadott_keszpenz !== null && !s.penz.rendben;
  const elterEKeszlet = (s: SoforAllapot) => s.keszlet.megszamolt_zaro !== null && !s.keszlet.rendben;

  return (
    `<div class="lap rovid">` +
    `<div class="rfej"><b>Napi kimutatás</b><div><span class="datum">${esc(nap)}</span>` +
    `<span class="all${napLezarva ? '' : ' nyitott'}">` +
    `${napLezarva ? 'lezárva' : `ellenőrzés alatt · ${lezartDb}/${soforok.length} sofőr lezárva`}` +
    `</span></div></div>` +
    `<div class="racs"><div>` +
    tetel('Liter', o.fplusz_liter ? `ebből F+ ${szam(o.fplusz_liter, 0)} l` : '', szam(o.liter, 0), 'l') +
    tetel('Összeg', 'bruttó eladás', ft(o.eladas), 'Ft') +
    tetel('Áfa', `${esc(alap.afa_kulcs)}%`, ft(o.afa), 'Ft') +
    tetel('Utalás', 'nem készpénzes: kártya, átutalás', ft(o.nem_keszpenzes), 'Ft') +
    `</div><div>` +
    tetel('Moslék', `${szam(o.mo_db, 0)} db`, ft(o.mo_osszeg), 'Ft') +
    tetel(
      'HSO',
      `${szam(o.hso_kg, 0)} kg · ebből készpénzben ${ft(o.hso_keszpenzes)} Ft`,
      ft(o.hso_osszeg),
      'Ft',
    ) +
    tetel('Kiadás', 'HSO nélkül', ft(o.egyeb_kiadas), 'Ft') +
    (o.beszedes ? tetel('Egyéb beszedés', '', ft(o.beszedes), 'Ft') : '') +
    `</div></div>` +
    // A kiadások tételesen, ha volt. (A megrendelő kérése, 2026-09-22)
    (kiadasTetelek.length > 0
      ? `<h3>Kiadások</h3><table>` +
        kiadasTetelek
          .map(
            (k) =>
              `<tr><td>${esc(k.kategoria_nev)}</td>` +
              `<td class="r">${k.keszpenzes !== k.osszeg ? `ebből kp ${ft(k.keszpenzes)} Ft` : ''}</td>` +
              `<td class="r">${ft(k.osszeg)} Ft</td></tr>`,
          )
          .join('') +
        // Egyetlen tételnél az összesen sor felesleges.
        (kiadasTetelek.length > 1
          ? `<tr class="ossz"><td>Összesen</td><td></td><td class="r">${ft(o.egyeb_kiadas)} Ft</td></tr>`
          : '') +
        `</table>`
      : '') +
    `<div class="leado"><em>LEADÓ</em><span>${ft(o.leado)}<i>Ft</i></span></div>` +
    `<div class="leadott"><div>Ténylegesen leadott: <b>${ft(leadott)} Ft</b>` +
    (leadottHianyzik ? ` (${leadottHianyzik} sofőrnél még nincs beírva)` : '') +
    `</div>` +
    (elteres !== null
      ? // A 100 Ft-on belüli eltérés kerekítés, nem hiba — nem piros. (2026-09-18)
        `<div class="${Math.abs(elteres) > 100 ? 'elt' : ''}">Eltérés: <b>${elteresSzoveg(elteres, 'Ft')}</b></div>`
      : '') +
    `</div>` +
    `<h3>Sofőrök</h3>` +
    `<table><tr><th>Sofőr</th><th class="r">Liter</th><th class="r">Összeg</th>` +
    `<th class="r">Leadó</th><th class="r">Leadott</th><th class="r">Záró készlet</th></tr>` +
    soforok
      .map(
        (s) =>
          `<tr><td>${esc(s.nev)}</td>` +
          `<td class="r">${szam(s.osszesites.liter, 0)}</td>` +
          `<td class="r">${ft(s.osszesites.eladas)}</td>` +
          `<td class="r">${ft(s.osszesites.leado)}</td>` +
          `<td class="r">${
            s.penz.leadott_keszpenz === null
              ? '—'
              : `<span class="${elterEPenz(s) ? 'elt' : ''}">${ft(s.penz.leadott_keszpenz)}</span>`
          }</td>` +
          `<td class="r">${
            s.keszlet.megszamolt_zaro === null
              ? '—'
              : `<span class="${elterEKeszlet(s) ? 'elt' : ''}">${szam(s.keszlet.megszamolt_zaro, 0)} l</span>`
          }</td></tr>`,
      )
      .join('') +
    `<tr class="ossz"><td>Összesen</td><td class="r">${szam(o.liter, 0)}</td>` +
    `<td class="r">${ft(o.eladas)}</td><td class="r">${ft(o.leado)}</td>` +
    `<td class="r">${ft(leadott)}</td><td></td></tr>` +
    `</table>` +
    `<div class="elab">Készült: ${esc(keszult)} · rövid napi kimutatás · ${esc(nap)}` +
    ` · piros: eltér a számítottól</div>` +
    `</div>`
  );
}

/** Az összesítő lap megnyitása — az Összesítő ellenőrzés gombja. */
export async function osszesitoElszamolasMentese({
  datum,
  ellenorzes,
  alapadatok,
}: {
  datum: string;
  ellenorzes: EllenorzesNap;
  alapadatok: DokumentumAlapadatok;
}): Promise<void> {
  const soforok = ellenorzes.soforok.filter((s) => s.sorok > 0 || s.megkezdte);
  if (soforok.length === 0 || !ellenorzes.osszesito_mind) {
    throw new Error('Még nincs kitöltött sor egyik sofőrnél sem.');
  }
  const w = ablakotNyit(CSS);
  try {
    const keszult = new Date().toLocaleString('hu-HU');
    dokumentumMegjelenites(
      w,
      `Elszámolás összesítő — ${huDatum(datum)}`,
      // A részletes lap után külön oldalon a rövid kimutatás. (2026-09-22)
      osszesitoLap(datum, ellenorzes, soforok, alapadatok, keszult) +
        rovidLap(datum, ellenorzes, soforok, alapadatok, keszult),
      CSS,
    );
  } catch (h) {
    ablakotBezar(w);
    throw h;
  }
}

/**
 * Az elszámolási lap(ok) megnyitása.
 *
 * Az ablak **még a kattintásban** nyílik (a böngésző csak így engedi), a
 * pénztételek betöltése után töltődik fel.
 */
export async function elszamolasMentese({
  datum,
  ellenorzes,
  soforok,
  alapadatok,
}: {
  datum: string;
  ellenorzes: EllenorzesNap;
  soforok: SoforAllapot[];
  alapadatok: DokumentumAlapadatok;
}): Promise<void> {
  if (soforok.length === 0) throw new Error('Még nincs kitöltött sor egyik sofőrnél sem.');

  // Ha a böngésző letiltja az ablakot, az elszámolás a felület fölött jelenik meg.
  const w = ablakotNyit(CSS);

  try {
    const tetelek = await Promise.all(
      soforok.map((s) =>
        api
          .get<{ tetelek: PenzTetel[] }>(`/api/ellenorzes/${datum}/penz/${s.sofor_id}`)
          .then((v) => v.tetelek),
      ),
    );
    const keszult = new Date().toLocaleString('hu-HU');
    const lapok = soforok
      .map((s, i) => lap(datum, ellenorzes, s, tetelek[i] ?? [], alapadatok, keszult))
      .join('');
    const cim =
      soforok.length === 1
        ? `Elszámolás — ${soforok[0]!.nev} · ${huDatum(datum)}`
        : `Elszámolás — ${huDatum(datum)}`;
    dokumentumMegjelenites(w, cim, lapok, CSS);
  } catch (h) {
    ablakotBezar(w);
    throw h;
  }
}
